import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload as UploadIcon, Loader2, FileDown, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Shell, TopBar, Disclaimer } from "../components/Shell";
import { api, API } from "../lib/api";

const T = {
  en: {
    intro: "For educational triage only — not a diagnosis. A dentist must review the actual radiograph.",
    upload: "Upload an X-ray or OPG",
    analyzing: "Analyzing…",
    hint: "JPG / PNG · upload the original digital X-ray, not a photo of a screen",
    findings: "Findings",
    none: "No significant findings",
    download: "Download report (PDF)",
    disclaimer: "For educational triage only — not a diagnosis. A dentist must review the actual radiograph.",
  },
  hi: {
    intro: "केवल शैक्षिक स्क्रीनिंग के लिए — यह कोई निदान नहीं है। असली एक्स-रे की जाँच दंत चिकित्सक द्वारा ही होनी चाहिए।",
    upload: "एक्स-रे या OPG अपलोड करें",
    analyzing: "जाँच हो रही है…",
    hint: "JPG / PNG · स्क्रीन की फोटो नहीं, असली डिजिटल एक्स-रे फ़ाइल अपलोड करें",
    findings: "निष्कर्ष",
    none: "कोई विशेष समस्या नहीं मिली",
    download: "रिपोर्ट डाउनलोड करें (PDF)",
    disclaimer: "केवल शैक्षिक स्क्रीनिंग के लिए — यह कोई निदान नहीं है। असली एक्स-रे की जाँच दंत चिकित्सक द्वारा ही होनी चाहिए।",
  },
};

export default function Xray() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [lang, setLang] = useState("en");
  const t = T[lang];

  const analyze = async (f) => {
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f, f.name || "xray.jpg");
      const r = await api.post("/xray/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
    } catch (e) {
      toast.error("Analysis failed. Please try another image.");
    } finally {
      setBusy(false);
    }
  };

  const issues = result?.issues || [];
  const hasHindi = issues.some((i) => i.label_hi) || result?.quality_note_hi;
  const qualityNote = lang === "hi" ? (result?.quality_note_hi || result?.quality_note) : result?.quality_note;
  const issueText = (i) => (lang === "hi" ? (i.label_hi || i.label) : i.label);

  return (
    <Shell header={<TopBar title="AI IOPA / OPG scan" onBack={() => navigate(-1)} />}>
      <div className="px-6 py-5 flex flex-col gap-5 pb-16">
        <Disclaimer tone="amber" testId="xray-disclaimer">{t.intro}</Disclaimer>

        <button data-testid="xray-upload" onClick={() => fileRef.current?.click()} disabled={busy}
          className="tap rounded-2xl border-2 border-dashed border-border bg-card p-8 flex flex-col items-center gap-2 text-primary">
          {busy ? <Loader2 className="w-8 h-8 animate-spin" /> : <UploadIcon className="w-8 h-8" />}
          <span className="font-semibold">{busy ? t.analyzing : t.upload}</span>
          <span className="text-xs text-muted-foreground text-center">{t.hint}</span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) analyze(f); }} />

        {result && (
          <div className="flex flex-col gap-4" data-testid="xray-result">
            {hasHindi && (
              <div className="flex items-center gap-1 self-start rounded-full bg-secondary p-1" data-testid="xray-lang-toggle">
                <button data-testid="xray-lang-en" onClick={() => setLang("en")}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${lang === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>English</button>
                <button data-testid="xray-lang-hi" onClick={() => setLang("hi")}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${lang === "hi" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>हिंदी</button>
              </div>
            )}

            <img src={`data:image/jpeg;base64,${result.annotated_image_base64}`} alt="Uploaded radiograph"
              className="w-full rounded-2xl border border-border" />

            <div className="rounded-2xl border border-border bg-card p-4" data-testid="xray-findings">
              <h3 className="font-head font-bold text-sm mb-3">{t.findings}</h3>
              {issues.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="xray-no-findings">{t.none}</p>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {issues.map((it, i) => (
                    <li key={i} data-testid={`xray-issue-${i}`}
                      className="text-[13px] font-semibold px-3 py-1.5 rounded-full bg-[#FCEBE8] text-[#9E2A16]">
                      {issueText(it)}
                    </li>
                  ))}
                </ul>
              )}
              {qualityNote && (
                <div className="flex gap-2 mt-3 text-xs text-amber-700 italic">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>{qualityNote}</span>
                </div>
              )}
            </div>

            {result.id && (
              <button data-testid="xray-download-pdf" onClick={() => window.open(`${API}/xray/${result.id}/report.pdf`, "_blank")}
                className="tap w-full h-13 py-3.5 rounded-2xl brand-gradient text-white font-semibold flex items-center justify-center gap-2">
                <FileDown className="w-4 h-4" /> {t.download}
              </button>
            )}

            <Disclaimer tone="amber">{result.disclaimer || t.disclaimer}</Disclaimer>
          </div>
        )}
      </div>
    </Shell>
  );
}
